import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Instagram, Check, Film, Image as ImageIcon, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { uploadSalonContent, getMyContent, contentFileUrl } from "../../lib/api";

const STATUS_STYLES = {
  submitted: "border-sand text-mocha",
  approved: "border-burgundy text-burgundy",
  posted: "border-espresso bg-espresso text-bone",
};

export default function ContentStudio() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [handle, setHandle] = useState("");
  const [caption, setCaption] = useState("");
  const [permission, setPermission] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try { setItems(await getMyContent()); } catch { /* empty */ }
  };
  useEffect(() => { load(); }, []);

  const pick = (f) => {
    if (!f) return;
    const isImage = f.type.startsWith("image/");
    const isVideo = f.type.startsWith("video/");
    if (!isImage && !isVideo) { toast.error("Only photos and videos are allowed."); return; }
    setFile(f);
    setPreview({ url: URL.createObjectURL(f), type: isVideo ? "video" : "image" });
  };

  const reset = () => {
    setFile(null); setPreview(null); setCaption(""); setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo or video first."); return; }
    if (!handle.trim()) { toast.error("Add your Instagram handle so we can credit you."); return; }
    if (!permission) { toast.error("Please tick the permission box."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("instagram_handle", handle.trim());
    fd.append("caption", caption.trim());
    fd.append("permission", "true");
    setUploading(true);
    try {
      await uploadSalonContent(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      toast.success("Sent to FYLLR — we'll tag you when it goes live. ✨");
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="mt-12" data-testid="content-studio">
      <div className="relative overflow-hidden rounded-2xl bg-espresso text-bone p-6 md:p-9">
        <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-burgundy/40 blur-3xl" aria-hidden />
        <div className="relative">
          <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/70"><Sparkles size={13} /> Content studio</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-[0.98]" data-testid="content-studio-heading">Send us your best shots.<br />We'll put you on the feed.</h2>
          <p className="mt-3 text-sm text-bone/70 max-w-lg">Share photos or short videos of your work. Approved pieces get posted to FYLLR's socials — always credited to your studio.</p>

          <form onSubmit={submit} className="mt-7 grid lg:grid-cols-2 gap-6" data-testid="content-studio-form">
            {/* Upload / preview */}
            <div>
              {!preview ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-bone/30 hover:border-bone/60 bg-bone/5 flex flex-col items-center justify-center gap-3 transition-colors"
                  data-testid="content-upload-trigger"
                >
                  <UploadCloud size={30} strokeWidth={1.3} className="text-bone/70" />
                  <span className="text-sm text-bone/70">Tap to upload a photo or video</span>
                  <span className="text-[11px] text-bone/45">JPG · PNG · WEBP · MP4 · MOV — up to 80MB</span>
                </button>
              ) : (
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-ink">
                  {preview.type === "video"
                    ? <video src={preview.url} className="h-full w-full object-cover" controls data-testid="content-preview-video" />
                    : <img src={preview.url} alt="preview" className="h-full w-full object-cover" data-testid="content-preview-image" />}
                  <button type="button" onClick={reset} className="absolute right-2 top-2 h-8 w-8 rounded-full bg-ink/70 text-bone flex items-center justify-center hover:bg-ink" data-testid="content-preview-clear" aria-label="Remove"><X size={15} /></button>
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 text-[10px] tracking-wide uppercase bg-bone/90 text-ink px-2 py-1 rounded">{preview.type === "video" ? <Film size={11} /> : <ImageIcon size={11} />}{preview.type}</span>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="content-file-input" />
              {uploading && (
                <div className="mt-3 h-1.5 rounded-full bg-bone/15 overflow-hidden" data-testid="content-progress">
                  <div className="h-full bg-bone transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>

            {/* Credit + caption */}
            <div className="space-y-4">
              <label className="block">
                <span className="block text-[11px] tracking-wide uppercase text-bone/55 mb-1.5">Instagram handle (for credit)</span>
                <div className="flex items-center gap-2 border border-bone/25 rounded-md px-3 py-2.5 focus-within:border-bone/60 transition-colors">
                  <Instagram size={16} className="text-bone/60" />
                  <input type="text" required value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@yourstudio" className="w-full bg-transparent text-sm text-bone placeholder:text-bone/40 outline-none" data-testid="content-handle" />
                </div>
              </label>
              <label className="block">
                <span className="block text-[11px] tracking-wide uppercase text-bone/55 mb-1.5">Caption / notes (optional)</span>
                <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} placeholder="Balayage by Sofia · Colour melt in warm honey tones…" className="w-full bg-bone/5 border border-bone/25 rounded-md px-3 py-2.5 text-sm text-bone placeholder:text-bone/40 outline-none focus:border-bone/60 transition-colors resize-none" data-testid="content-caption" />
              </label>
              <button type="button" onClick={() => setPermission(!permission)} className="flex items-start gap-3 text-left" data-testid="content-permission" aria-pressed={permission}>
                <span className={`mt-0.5 shrink-0 h-5 w-5 rounded border flex items-center justify-center transition-colors ${permission ? "bg-bone border-bone" : "border-bone/40"}`}>{permission && <Check size={13} className="text-espresso" />}</span>
                <span className="text-[13px] text-bone/75">I give FYLLR permission to post this content on its social channels, credited to my studio.</span>
              </button>
              <button type="submit" disabled={uploading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-bone text-espresso py-3 text-sm font-medium hover:bg-sand disabled:opacity-60 transition-colors" data-testid="content-submit">
                {uploading ? <><Loader2 size={15} className="animate-spin" /> Uploading… {progress}%</> : "Send to FYLLR"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Submissions */}
      {items.length > 0 && (
        <div className="mt-6" data-testid="content-submissions">
          <p className="text-xs text-ink/45 mb-3">Your submissions</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((item) => (
              <div key={item.id} className="group relative aspect-square rounded-lg overflow-hidden border border-sand/70 bg-ink/5" data-testid={`content-item-${item.id}`}>
                {item.media_type === "video"
                  ? <video src={contentFileUrl(item.storage_path)} className="h-full w-full object-cover" muted />
                  : <img src={contentFileUrl(item.storage_path)} alt={item.caption || "submission"} className="h-full w-full object-cover" />}
                <span className={`absolute left-2 top-2 text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[item.status] || STATUS_STYLES.submitted}`} data-testid={`content-status-${item.id}`}>{item.status}</span>
                {item.media_type === "video" && <Film size={14} className="absolute right-2 top-2 text-bone drop-shadow" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
